import { SquareArrowOutUpRight } from 'lucide-react'
import type { PlayerCardData, ExtendedBattingLine, ExtendedPitchingLine, FieldingLine } from './types'
import { StatTable } from './sections/StatsSection'
import { avg3 } from './format'
import { toPer160, toPerInnings } from './per160'
import { cardTheadRow, cardBodyRow, cardTotalRow, cardStickyBg } from './cardTableStyles'
import { CardTeamLink } from './CardTeamLink'

// The "scroll everything" tab. Every stat table renders at full width (tier 2)
// with no collapsing — if you want the deep cuts, you scroll. Summary keeps the
// lean snapshot + simplified splits; this is the firehose.
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="py-3 first:pt-1 last:pb-1">
      <h3 className="mb-1.5 text-sm font-bold uppercase tracking-wide text-navy">{title}</h3>
      {children}
    </section>
  )
}


const num = (v: number) => v.toLocaleString('en-US')
const pct = (v: number) => `${v}%`
const f1 = (v: number) => v.toFixed(1)
const f2 = (v: number) => v.toFixed(2)

// Legacy "Batting Stats - Extended" column set.
type ExtCol = { key: keyof ExtendedBattingLine; label: string; fmt: (v: number) => string }
const EXT_COLS: ExtCol[] = [
  { key: 'pa', label: 'PA', fmt: num },
  { key: 'tb', label: 'TB', fmt: num },
  { key: 'gbfb', label: 'GBFB', fmt: pct },
  { key: 'sf', label: 'SF', fmt: String },
  { key: 'hbp', label: 'HBP', fmt: String },
  { key: 'gdp', label: 'GDP', fmt: String },
  { key: 'roe', label: 'ROE', fmt: String },
  { key: 'xb', label: 'XB', fmt: String },
  { key: 'bro', label: 'BRO', fmt: String },
  { key: 'sa', label: 'SA', fmt: avg3 },
  { key: 'rc', label: 'RC', fmt: f1 },
  { key: 'lwts', label: 'LWTS', fmt: f2 },
  { key: 'abhr', label: 'ABHR', fmt: f2 },
  { key: 'tbpa', label: 'TBPA', fmt: f2 },
]

// Legacy "Fielding Stats" column set. Year + Pos anchor; SB/CS/CSPct are blank
// for non-catchers.
type FldCol = { key: keyof FieldingLine; label: string; fmt: (v: number) => string }
const FLD_COLS: FldCol[] = [
  { key: 'g', label: 'G', fmt: String },
  { key: 'po', label: 'PO', fmt: num },
  { key: 'a', label: 'A', fmt: num },
  { key: 'e', label: 'E', fmt: String },
  { key: 'tc', label: 'TC', fmt: num },
  { key: 'dp', label: 'DP', fmt: String },
  { key: 'sb', label: 'SB', fmt: String },
  { key: 'cs', label: 'CS', fmt: String },
  { key: 'csPct', label: 'CSPct', fmt: (v) => `${v.toFixed(1)}%` },
  { key: 'pct', label: 'PCT', fmt: avg3 },
  { key: 'rng', label: 'RNG', fmt: f2 },
]

function FieldingTable({ rows }: { rows: FieldingLine[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm tabular-nums">
        <thead>
          <tr className={cardTheadRow}>
            <th className="sticky left-0 z-20 !bg-white py-1.5 pr-3 text-left font-semibold">Year</th>
            <th className="px-2 py-1.5 text-left font-semibold">Pos</th>
            {FLD_COLS.map((c) => <th key={c.label} className="px-2 py-1.5 text-right font-semibold">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((line, i) => {
            const isTotal = line.year === 'Career'
            return (
              <tr key={i} className={isTotal ? cardTotalRow : cardBodyRow}>
                <td className={`sticky left-0 z-10 ${isTotal ? '!bg-slate-100' : cardStickyBg(i)} py-1.5 pr-3 text-left whitespace-nowrap`}><span className="font-semibold">{line.year}</span><CardTeamLink teamId={line.teamId} team={line.team} /></td>
                <td className="px-2 py-1.5 text-left font-medium">{line.pos}</td>
                {FLD_COLS.map((c) => {
                  const v = line[c.key]
                  return <td key={c.label} className="px-2 py-1.5 text-right">{typeof v === 'number' ? c.fmt(v) : '—'}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Legacy "Pitching Stats - Extended" column set.
type ExtPCol = { key: keyof ExtendedPitchingLine; label: string; fmt: (v: number) => string }
const EXT_PIT_COLS: ExtPCol[] = [
  { key: 'so9', label: 'SO9', fmt: f1 },
  { key: 'bb9', label: 'BB9', fmt: f1 },
  { key: 'sobb', label: 'SOBB', fmt: f2 },
  { key: 'go', label: 'GO', fmt: num },
  { key: 'fo', label: 'FO', fmt: num },
  { key: 'ffo', label: 'FFO', fmt: num },
  { key: 'hr', label: 'HR', fmt: String },
  { key: 'ir', label: 'IR', fmt: String },
  { key: 'irs', label: 'IRS', fmt: String },
  { key: 'oavg', label: 'OAVG', fmt: avg3 },
  { key: 'oobp', label: 'OOBP', fmt: avg3 },
  { key: 'oslg', label: 'OSLG', fmt: avg3 },
  { key: 'oops', label: 'OOPS', fmt: avg3 },
  { key: 'lwts', label: 'LWTS', fmt: f2 },
  { key: 'babip', label: 'BABIP', fmt: avg3 },
]

function ExtendedPitchingTable({ rows }: { rows: ExtendedPitchingLine[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm tabular-nums">
        <thead>
          <tr className={cardTheadRow}>
            <th className="sticky left-0 z-20 !bg-white py-1.5 pr-3 text-left font-semibold">Year</th>
            {EXT_PIT_COLS.map((c) => <th key={c.label} className="px-2 py-1.5 text-right font-semibold">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((line, i) => {
            const isTotal = line.label === 'Career'
            return (
              <tr key={i} className={isTotal ? cardTotalRow : cardBodyRow}>
                <td className={`sticky left-0 z-10 ${isTotal ? '!bg-slate-100' : cardStickyBg(i)} py-1.5 pr-3 text-left whitespace-nowrap`}><span className="font-semibold">{line.label}</span><CardTeamLink teamId={line.teamId} team={line.team} /></td>
                {EXT_PIT_COLS.map((c) => {
                  const v = line[c.key]
                  return <td key={c.label} className="px-2 py-1.5 text-right">{typeof v === 'number' ? c.fmt(v) : '—'}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ExtendedBattingTable({ rows }: { rows: ExtendedBattingLine[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm tabular-nums">
        <thead>
          <tr className={cardTheadRow}>
            <th className="sticky left-0 z-20 !bg-white py-1.5 pr-3 text-left font-semibold">Year</th>
            {EXT_COLS.map((c) => <th key={c.label} className="px-2 py-1.5 text-right font-semibold">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((line, i) => {
            const isTotal = line.label === 'Career'
            return (
              <tr key={i} className={isTotal ? cardTotalRow : cardBodyRow}>
                <td className={`sticky left-0 z-10 ${isTotal ? '!bg-slate-100' : cardStickyBg(i)} py-1.5 pr-3 text-left whitespace-nowrap`}><span className="font-semibold">{line.label}</span><CardTeamLink teamId={line.teamId} team={line.team} /></td>
                {EXT_COLS.map((c) => {
                  const v = line[c.key]
                  return <td key={c.label} className="px-2 py-1.5 text-right">{typeof v === 'number' ? c.fmt(v) : '—'}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function FullStatsTab({ player }: { player: PlayerCardData }) {
  const seasons = player.seasonStats ?? []
  // "Per 160 games" only makes sense for position players. Pitchers don't appear
  // in 160 games (a starter ~32), so a games-based projection balloons to ~1053
  // IP / 80 W — project them on innings instead (a full 200-IP season).
  const projected = player.isPitcher ? toPerInnings(seasons) : toPer160(seasons)
  const projTitle = player.isPitcher ? 'Per 200 IP' : 'Per 160 Games'
  const projNote = player.isPitcher
    ? 'Counting stats projected to a 200-inning pace. Rate stats unchanged.'
    : 'Counting stats projected to a 160-game pace. Rate stats unchanged.'
  const extended = player.extendedBattingStats ?? []
  const extendedPit = player.extendedPitchingStats ?? []
  const fielding = player.fieldingStats ?? []

  // Full Stats = the player's own three season tables at full width (batting/
  // pitching, extended, fielding). Splits / situational live (with history +
  // career) on the Summary tab; the firehose — including playoffs — lives on the
  // standalone full player page, linked below.
  return (
    <div className="divide-y divide-slate-100 py-1">
      <Section title={player.isPitcher ? 'Pitching' : 'Batting'}>
        {seasons.length > 0
          ? <StatTable isPitcher={player.isPitcher} rows={seasons} tier={2} leaders={player.statLeaders} records={player.statRecords} />
          : <p className="text-sm text-slate-500">No season stats yet.</p>}
      </Section>
      {seasons.length > 0 && (
        <Section title={projTitle}>
          {/* Counting stats projected to a full-season pace (batters ÷ G × 160;
              pitchers ÷ IP × 200); rate stats are left as-is. No leader/record
              bold here — those flags describe actual season totals, not the pace. */}
          <p className="mb-1.5 text-xs text-slate-500">{projNote}</p>
          <StatTable isPitcher={player.isPitcher} rows={projected} tier={2} />
        </Section>
      )}
      {player.isPitcher ? (
        <Section title="Pitching Extended">
          {extendedPit.length > 0
            ? <ExtendedPitchingTable rows={extendedPit} />
            : <p className="text-sm text-slate-500">No extended pitching stats.</p>}
        </Section>
      ) : (
        <Section title="Batting Extended">
          {extended.length > 0
            ? <ExtendedBattingTable rows={extended} />
            : <p className="text-sm text-slate-500">No extended batting stats.</p>}
        </Section>
      )}
      <Section title="Fielding">
        {fielding.length > 0
          ? <FieldingTable rows={fielding} />
          : <p className="text-sm text-slate-500">No fielding stats.</p>}
      </Section>
      <section className="py-3 first:pt-1 last:pb-1">
        <p className="text-sm text-slate-500">
          Situational, splits, playoff, and game-log stats are on the{' '}
          <a
            href={`/player/${player.id}/view`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-navy hover:underline"
          >
            full player page
            <SquareArrowOutUpRight className="h-3.5 w-3.5" />
          </a>
        </p>
      </section>
    </div>
  )
}
